import { t } from '@/features/i18n/utils';
import { BidComplianceCheck } from '@/features/bid-compliance/components/bid-compliance-check';
import { useProjectView } from '@/features/projects/helpers';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import {
  BID_COMPLIANCE_CATEGORIES,
  BidComplianceStatus,
  COMPLIANCE_BUCKETS,
} from '@/features/bid-compliance/constants';
import { bidComplianceQuery } from '@/features/bid-compliance/queries';
import { useComplianceSection } from '@/features/bid-compliance/helpers';
import { complianceCategoryLabel } from '@/features/bid-compliance/utils';
import { BidComplianceCategoryPage } from '@/features/bid-compliance/components/bid-compliance-category-page';
import { NotFound } from '@/ui/components/not-found';
import z from 'zod';
import { BID_OVERVIEW_TABS } from '@/features/bids/constants';
import { bidDetailsQuery } from '@/features/bids/queries';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/compliance/$categoryId/',
)({
  staticData: { chatSurface: 'bid_compliance' },
  loader: ({ context, params }) =>
    context.queryClient.query({
      ...bidComplianceQuery(params.workspaceId, params.bidId),
      staleTime: 'static',
    }),
  component: RouteComponent,
  validateSearch: z.object({
    bucket: z.enum(COMPLIANCE_BUCKETS).optional().catch(undefined),
    tab: z.enum(BID_OVERVIEW_TABS).optional().catch(undefined),
  }),
});

function RouteComponent() {
  const view = useProjectView();
  const { bidId, workspaceId, projectId, categoryId } = Route.useParams();
  const search = Route.useSearch();
  const { data: bid } = useSuspenseQuery(bidDetailsQuery(bidId));
  const { data: compliance } = useSuspenseQuery(bidComplianceQuery(workspaceId, bidId));
  const section = useComplianceSection();
  const knownCategory = BID_COMPLIANCE_CATEGORIES.includes(categoryId as never);
  if (!section && (!knownCategory || compliance.status === BidComplianceStatus.DONE))
    return <NotFound />;

  const categoryLabel = knownCategory ? complianceCategoryLabel(categoryId, view) : section?.label;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
          params={{ projectId, workspaceId, bidId }}
          search={{ tab: search.tab ?? 'compliance' }}
        />
        <BidBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
            params={{ projectId, workspaceId, bidId }}
          >
            {bid.bidType === 'PREQUALIFICATION'
              ? t({ en: 'Qualification checklist', nb: 'Kvalifiseringssjekkliste' })
              : t({ en: 'Bid compliance checklist', nb: 'Kravsjekk' })}
          </BreadcrumbLink>
          <BreadcrumbPage>{categoryLabel}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      {section ? <BidComplianceCategoryPage section={section} /> : <BidComplianceCheck />}
    </>
  );
}
